import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import { toastError, toastInfo, toastSuccess } from "../../shared/toastHelper";
import SeriesEditHeader from "./components/SeriesEditHeader";
import SeriesSetupNav from "./components/SeriesSetupNav";
import SeriesDetailsSection from "./components/SeriesDetailsSection";
import ScheduleSection from "./components/ScheduleSection";
import TicketConfigSection from "./components/TicketConfigSection";
import ReviewSection from "./components/ReviewSection";
import DisplaySection from "./components/DisplaySection";
import AutomationsSection from "./components/AutomationsSection";
import SeriesSettingsSection from "./components/SeriesSettingsSection";
import PropagateChangesModal from "./components/PropagateChangesModal";
import ApplySeriesDetailsModal from "./components/ApplySeriesDetailsModal";
import UpdateDatesModal from "./components/UpdateDatesModal";
import {
  blankShow,
  allTicketTypes,
  scheduleSlotRows,
  plannedTicketTypeRows,
  plannedAddOnRows,
  pendingAddOnImages,
  plannedPromoCodeRows,
  templateIdsByRow,
  plannedMembershipRows,
  showTypeSettingRows,
  plannedHoldRows,
  plannedKillRows,
  showDetailTimeRows,
  configFor,
  blankTicketType,
  timedEntryProblems,
  TIMED_ENTRY_SHOW_TYPE,
} from "./scheduleBuilder";
import { ticketSetRows } from "./ticketSets";
import { BLANK_FEES, feeComponentRows, feeRuleOverrideRows } from "./seriesFeeStores";
import { initialSettings, pendingSponsorLogos, seriesSettingsRow, showTypeCopyRow, statementDescriptorProblem } from "./seriesSettings";
import { appendDateUpdateParams, plural } from "./updateDates";
import "./styles/seriesEditStyles.css";

const UNTITLED_SERIES = "Untitled series";

export const SeriesEditContext = createContext();
export const useSeriesEditContext = () => useContext(SeriesEditContext);

const DEFAULT_ENTRY = { firstEntry: "18:00", lastEntry: "22:00", intervalMinutes: 60, capacity: "" };

const savedEntry = (saved) =>
  Object.fromEntries(
    Object.entries((saved && saved.entry) || {}).filter(([, value]) => value !== "" && value != null),
  );

const initialSchedule = (series, setupConfig) => {
  const saved = setupConfig && setupConfig.schedule;
  if (saved && (saved.generated || []).length > 0) {
    return {
      ...saved,
      showTimes: saved.showTimes || {},
      entry: { ...DEFAULT_ENTRY, ...savedEntry(saved) },
    };
  }

  const daysOfWeek = (saved && saved.daysOfWeek) || series.daysOfWeek || [];
  const showTimes = {};
  daysOfWeek.forEach((day) => {
    showTimes[day] = [blankShow()];
  });
  return {
    mode: (saved && saved.mode) || "multiple_shows",
    startDate: (saved && saved.startDate) || series.startDate || "",
    endDate: (saved && saved.endDate) || series.endDate || "",
    daysOfWeek,
    showTimes,
    entry: { ...DEFAULT_ENTRY, ...savedEntry(saved) },
    generated: [],
  };
};

const initialTickets = (series, setupConfig) => {
  const saved = setupConfig && setupConfig.tickets;
  return {
    seatingMode: (saved && saved.seatingMode) || "general_admission",
    capacity:
      saved && saved.capacity
        ? saved.capacity
        : series.capacity == null
          ? ""
          : String(series.capacity),
    byShowType: (saved && saved.byShowType) || {},
    fees: (saved && saved.fees) || BLANK_FEES,
  };
};

const appendWizardConfig = (data, { schedule, tickets, settings, venueDefaults, uploadedAddOnImages, uploadedSponsorLogos, form, templateIds, planFiles }) => {
  data.append("event_series[schedule_mode]", schedule.mode || "");
  data.append("event_series[entry_first_time]", schedule.entry.firstEntry || "");
  data.append("event_series[entry_last_time]", schedule.entry.lastEntry || "");
  data.append("event_series[entry_interval_minutes]", schedule.entry.intervalMinutes || "");
  data.append("event_series[entry_capacity]", schedule.entry.capacity || "");
  data.append("event_series[seating_mode]", tickets.seatingMode || "");
  data.append("event_series[event_capacity]", tickets.capacity || "");
  data.append("event_series[general_ledger_code_id]", (tickets.fees && tickets.fees.generalLedgerCodeId) || "");
  data.append(
    "event_series[wizard_config_json]",
    JSON.stringify({
      schedule_slots: scheduleSlotRows(schedule, tickets),
      ticket_sets: ticketSetRows(schedule, configFor(tickets.byShowType, TIMED_ENTRY_SHOW_TYPE)),
      planned_ticket_types: plannedTicketTypeRows(schedule, tickets, templateIds),
      planned_add_ons: plannedAddOnRows(schedule, tickets),
      planned_promo_codes: plannedPromoCodeRows(schedule, tickets, templateIds),
      planned_membership_products: plannedMembershipRows(schedule, tickets),
      show_type_settings: showTypeSettingRows(schedule, tickets, venueDefaults).map((row) => ({
        ...row,
        ...showTypeCopyRow(settings.byShowType[row.show_type]),
      })),
      planned_holds: plannedHoldRows(schedule, tickets),
      planned_kills: plannedKillRows(schedule, tickets),
      show_detail_times: showDetailTimeRows(form.showDetailTimes, form.showDetailOffsets),
      organizer_fee_components: feeComponentRows(tickets.fees),
      fee_rule_overrides: feeRuleOverrideRows(tickets.fees),
      series_settings: seriesSettingsRow(settings),
    }),
  );
  if (!planFiles) return;
  pendingAddOnImages(schedule, tickets, uploadedAddOnImages).forEach((addOn) => {
    data.append(`event_series[add_on_images][${addOn.clientKey}]`, addOn.imageFile);
  });
  pendingSponsorLogos(settings, uploadedSponsorLogos).forEach((logo) => {
    data.append(`event_series[sponsor_logos][${logo.key}]`, logo.file);
  });
};

export const flattenErrors = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    return Object.entries(raw).flatMap(([field, messages]) =>
      [].concat(messages).map((message) => (field === "base" ? message : `${field} ${message}`)),
    );
  }
  return [];
};

export const AUTOSAVE_DELAY_MS = 2000;

const PROPAGATED_FIELDS = ["name", "description", "eventTypeTagId", "ageRestriction", "eventTemplateId"];

export const changesEventFields = (form, saved, { photoFile, removePhoto }) =>
  PROPAGATED_FIELDS.some((key) => String(form[key] ?? "") !== String(saved[key] ?? "")) || !!photoFile || !!removePhoto;

const errorsFrom = (e) => flattenErrors(e && e.response && e.response.data && e.response.data.errors);

const DEFAULT_DATE_UPDATE = { scope: "all", from: "", selectedIds: [], ownValues: "keep", chosen: {} };

// What the dates did, when the rest of the series then fails to save.
const unsavedSeriesMessage = (dateUpdate) => {
  if (dateUpdate && dateUpdate.queued_count) {
    return `Updating ${dateUpdate.queued_count} ${plural(dateUpdate.queued_count, "date")} in the background, but the rest of the series didn't save.`;
  }
  if (!dateUpdate || !dateUpdate.applied_count) return "The rest of the series didn't save.";
  return "The dates updated, but the rest of the series didn't save.";
};

export const dateUpdateToast = (dateUpdate) => {
  if (!dateUpdate) return [];
  if (dateUpdate.queued_count) {
    return [{ tone: "success", message: `Updating ${dateUpdate.queued_count} ${plural(dateUpdate.queued_count, "date")} in the background. We'll email you when it's done.` }];
  }
  const applied = dateUpdate.applied_count || 0;
  const skipped = (dateUpdate.skipped || []).map((skip) => ({ tone: "info", message: `${skip.name} (${skip.date}) wasn't updated: ${skip.reason}` }));
  // applied_count counts only dates that changed, so 0 means every date kept its values.
  return [...(applied > 0 ? [{ tone: "success", message: `${applied} ${plural(applied, "date")} updated.` }] : []), ...skipped];
};

const SeriesEditApp = (props) => {
  const { csrfToken, options, indexUrl, showUrl, updateUrl, publishUrl,
    setupConfig = null, setupWizardEnabled = false, setupWizardAvailable = false } = props;
  // The Series Settings step and the settings it adds to other steps only save with the wizard flag on.
  const seriesSettingsEnabled = setupWizardEnabled && !!props.seriesSettingsEnabled;
  const [form, setForm] = useState(props.series);
  const [photoFile, setPhotoFile] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [applyDetailsOpen, setApplyDetailsOpen] = useState(false);
  const [updatePreview, setUpdatePreview] = useState(null);
  // The series as last saved: what a legacy save compares against to ask
  // about applying to the events, and the name the update modal shows.
  const savedSeries = useRef(props.series);
  const [step, setStep] = useState("details");
  const [schedule, setSchedule] = useState(() => initialSchedule(props.series, setupConfig));
  const [tickets, setTickets] = useState(() => initialTickets(props.series, setupConfig));
  const [settings, setSettings] = useState(() => initialSettings(setupConfig));
  const [distributionTermsAgreed, setDistributionTermsAgreed] = useState(Boolean(options.agreedToOpendateDistributionTermsAt));
  const [ticketTemplates, setTicketTemplates] = useState(() => options.ticketTemplates || []);
  const selectedVenue = (options.venues || []).find((venue) => venue.value === form.venueOwnershipId);
  const venueDefaults = selectedVenue ? selectedVenue.defaults : undefined;
  const extraShowDetailNames = (selectedVenue && selectedVenue.extraShowDetailNames) || [];
  const windowNames = [...(options.showActivityNames || []), ...extraShowDetailNames];
  const [publishErrors, setPublishErrors] = useState([]);
  const [publishWarnings, setPublishWarnings] = useState([]);
  const [autoSave, setAutoSave] = useState({ status: "idle", savedAt: null, error: null });
  const autoSaveTimer = useRef(null);
  const autoSaveInFlight = useRef(false);
  const autoSaveArmed = useRef(false);
  const uploadedPhoto = useRef(null);
  const uploadedAddOnImages = useRef(new WeakSet());
  const uploadedSponsorLogos = useRef(new WeakSet());
  const runAutoSave = useRef(() => {});
  const templateIds = useRef({});
  const autoSaveEnabled = setupWizardEnabled && !!form.isDraft;
  const savingRef = useRef(false);
  // What the open update modal's first preview was built from, and each
  // section ticket type the modal priced, so going back undoes only those.
  const previewed = useRef(null);
  const sectionEdits = useRef(new Map());
  const latest = useRef(null);
  const finishSaving = () => {
    savingRef.current = false;
    setSaving(false);
  };
  const newPhoto = () => (photoFile && photoFile !== uploadedPhoto.current ? photoFile : null);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const canSubmit = (form.name || "").trim().length > 0 && !!form.eventTypeTagId;
  const canSave = form.isDraft ? true : canSubmit;

  // ticketsLanding lets Review open Ticket Config on a given show type and sub-tab.
  const [ticketsLanding, setTicketsLanding] = useState(null);
  const goToStep = (next, landing = null) => {
    setTicketsLanding(landing);
    setStep(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const configuredTicketTypes = useMemo(() => allTicketTypes(tickets.byShowType), [tickets.byShowType]);
  const ticketSetProblems = useMemo(
    () => timedEntryProblems(schedule, tickets, form.ageRestriction),
    [schedule, tickets, form.ageRestriction],
  );

  const completion = useMemo(
    () => ({
      details: canSubmit ? "complete" : "incomplete",
      schedule: schedule.generated.length > 0 ? "complete" : "incomplete",
      tickets: configuredTicketTypes.length > 0 && ticketSetProblems.length === 0 ? "complete" : "incomplete",
      review: null,
    }),
    [canSubmit, schedule.generated.length, configuredTicketTypes.length, ticketSetProblems.length],
  );

  // A published wizard series saves its title, description and age through the
  // date update, so the plain save leaves them out and they have one writer.
  const tracksOwnValues = setupWizardEnabled && !!props.series.tracksOwnValues;
  // With date syncing off (O-7614) a published wizard series edits like a series
  // without the wizard: details and public page only, and its plan stays as published.
  const lockedPublished = setupWizardEnabled && !tracksOwnValues && !props.series.isDraft;
  const wizardSteps = setupWizardEnabled && !lockedPublished;

  // withDateUpdateFields: the title, description, age, event type and template,
  // which a published wizard series saves through the date update instead.
  // photo: a new series photo (the date update takes it on create, never on the
  // preview). removePhoto: only the plain save takes a removal. planFiles: new
  // add-on images and sponsor logos, which a published wizard series sends with
  // the date update instead, so they reach its dates.
  const seriesFormData = (propagate, { withDateUpdateFields = true, photo = true, removal = true, planFiles = true } = {}) => {
    const data = new FormData();
    if (withDateUpdateFields) {
      data.append("event_series[name]", (form.name || "").trim() || UNTITLED_SERIES);
      data.append("event_series[description]", form.description || "");
      data.append("event_series[age_restriction]", form.ageRestriction || "");
      if (form.eventTypeTagId) data.append("event_series[event_type_tag_id]", form.eventTypeTagId);
      if (form.eventTemplateId) data.append("event_series[event_template_id]", form.eventTemplateId);
    }
    data.append("event_series[subtitle]", form.subtitle || "");
    if (photo && newPhoto()) data.append("event_series[website_event_image]", photoFile);
    if (removal && removePhoto) data.append("event_series[remove_series_photo]", "true");
    data.append("event_series[propagate_to_events]", propagate ? "true" : "false");
    data.append("event_series[date_list_heading]", form.dateListHeading || "");
    data.append("event_series[date_list_instructions]", form.dateListInstructions || "");
    data.append("event_series[ticket_note]", form.ticketNote || "");
    data.append("event_series[show_event_details_on_public_page]", form.showEventDetailsOnPublicPage ? "true" : "false");
    if (wizardSteps) {
      Object.entries(form.showActivityTimes || {}).forEach(([key, value]) => {
        data.append(`event_series[${key}]`, value || "");
      });
      if (form.venueOwnershipId) {
        data.append("event_series[venue_ownership_id]", form.venueOwnershipId);
      }
      if (form.isDraft) data.append("event_series[shows_publish_at]", form.showsPublishAt || "");
      appendWizardConfig(data, {
        schedule, tickets, settings, venueDefaults, form,
        uploadedAddOnImages: uploadedAddOnImages.current,
        uploadedSponsorLogos: uploadedSponsorLogos.current,
        templateIds: templateIds.current,
        planFiles,
      });
    }

    return data;
  };

  const patchSeries = (propagate) => {
    const sent = tickets;
    return axios
      .patch(
        `${updateUrl}.json`,
        seriesFormData(tracksOwnValues ? false : propagate, {
          withDateUpdateFields: !tracksOwnValues, photo: !tracksOwnValues, planFiles: !tracksOwnValues,
        }),
        { headers: { "X-CSRF-TOKEN": csrfToken } },
      )
      .then((response) => {
        templateIds.current = templateIdsByRow(sent, response && response.data && response.data.planTemplateIds, templateIds.current);
        return response;
      });
  };

  // The preview never takes files (it rolls back, and a written file would stay).
  // A removed photo has no date-update contract, so it stays with the plain save.
  const dateUpdateData = (params, { files }) =>
    appendDateUpdateParams(seriesFormData(false, { photo: files, removal: false, planFiles: files }), params);

  const pendingPlanFiles = () => ({
    photo: newPhoto(),
    addOnImages: setupWizardEnabled ? pendingAddOnImages(schedule, tickets, uploadedAddOnImages.current) : [],
    sponsorLogos: setupWizardEnabled ? pendingSponsorLogos(settings, uploadedSponsorLogos.current) : [],
  });

  const markUploaded = ({ photo, addOnImages, sponsorLogos }) => {
    if (photo) uploadedPhoto.current = photo;
    addOnImages.forEach((addOn) => uploadedAddOnImages.current.add(addOn.imageFile));
    sponsorLogos.forEach((logo) => uploadedSponsorLogos.current.add(logo.file));
  };

  // A published wizard series stays on the page after a save. savedSeries
  // keeps the saved series for the update modal's title; the next save asks
  // the preview what changed, not savedSeries. Its photo goes with the date
  // update, which marks it uploaded. The save ran on the state at its click,
  // so only a removal it sent is cleared: one made while it ran waits for the
  // next save.
  const savedInPlace = ({ sentRemoval = removePhoto, dates = [] } = {}) => {
    savedSeries.current = form;
    if (sentRemoval) setRemovePhoto(false);
    toastSuccess(dates.length > 0 ? ["Series changes saved.", ...dates].join(" ") : "Series changes saved");
  };

  const fetchDateUpdatePreview = (params) =>
    axios
      .post(`${updateUrl}/date_update/new.json`, dateUpdateData(params, { files: false }), { headers: { "X-CSRF-TOKEN": csrfToken } })
      .then((response) => response.data);

  // A new section on a swapped chart is priced in the update modal; the price
  // lands on the section's ticket type so the plan carries it.
  const sectionType = (showType, key) =>
    configFor(tickets.byShowType, showType).ticketTypes.find((type) => type.sectionKey === key);
  const sectionPrice = (showType, key) => {
    const type = sectionType(showType, key);
    return type && type.price !== undefined && type.price !== null ? String(type.price) : "";
  };
  const sectionFree = (showType, key) => Boolean((sectionType(showType, key) || {}).free);
  // Another show type's ticket type for the same section, or one named like it.
  const matchingSectionPrice = (showType, section) => {
    const label = (section.label || "").trim().toLowerCase();
    const match = allTicketTypes(tickets.byShowType).find((type) => parseFloat(type.price) > 0
      && (type.sectionKey === section.key || (type.name || "").trim().toLowerCase() === label));
    return match ? String(match.price) : null;
  };
  const setSectionPrice = (showType, section, price) => {
    setTickets((current) => {
      const config = configFor(current.byShowType, showType);
      const existing = config.ticketTypes.find((type) => type.sectionKey === section.key);
      const exists = Boolean(existing);
      const editKey = JSON.stringify([showType, section.key]);
      if (!sectionEdits.current.has(editKey)) {
        sectionEdits.current.set(editKey, { showType, key: section.key, added: !exists, price: existing && existing.price });
      }
      const ticketTypes = exists
        ? config.ticketTypes.map((type) => (type.sectionKey === section.key ? { ...type, price } : type))
        : [...config.ticketTypes, { ...blankTicketType(), name: section.label, sectionKey: section.key, price }];
      return { ...current, byShowType: { ...current.byShowType, [showType]: { ...config, ticketTypes } } };
    });
  };

  // Runs on the state at the click that started it: the trailing save sends
  // the same plan the update staged, so nothing edited meanwhile slips past
  // the update; those edits wait for the next save.
  const runDateUpdate = async (params) => {
    const sent = tickets;
    const sentRemoval = removePhoto;
    const files = pendingPlanFiles();
    let response;
    try {
      response = await axios.post(`${updateUrl}/date_update.json`, dateUpdateData(params, { files: true }), { headers: { "X-CSRF-TOKEN": csrfToken } });
    } catch (e) {
      const errors = errorsFrom(e);
      toastError(errors.length > 0 ? errors.join(" ") : "Could not update the dates. Please try again.");
      finishSaving();
      return;
    }
    markUploaded(files);
    templateIds.current = templateIdsByRow(sent, response.data && response.data.planTemplateIds, templateIds.current);
    sectionEdits.current = new Map();
    savedSeries.current = {
      ...savedSeries.current,
      name: form.name, description: form.description, ageRestriction: form.ageRestriction,
      eventTypeTagId: form.eventTypeTagId, eventTemplateId: form.eventTemplateId,
    };
    setUpdatePreview(null);
    // What the dates did joins the save's own toast, once the save lands.
    const toasts = dateUpdateToast(response.data && response.data.dateUpdate);
    const done = toasts.filter(({ tone }) => tone === "success");
    toasts.filter(({ tone }) => tone !== "success").forEach(({ message }) => toastInfo(message));
    try {
      await patchSeries(false);
      savedInPlace({ sentRemoval, dates: done.map(({ message }) => message) });
    } catch (e) {
      const errors = errorsFrom(e);
      toastError(`${unsavedSeriesMessage(response.data && response.data.dateUpdate)}${errors.length > 0 ? ` ${errors.join(" ")}` : ""}`);
    } finally {
      finishSaving();
    }
  };

  const applyDateUpdate = (params) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    runDateUpdate(params);
  };

  runAutoSave.current = async () => {
    if (saving) return;
    if (autoSaveInFlight.current) {
      autoSaveTimer.current = setTimeout(() => runAutoSave.current(), AUTOSAVE_DELAY_MS);
      return;
    }
    autoSaveInFlight.current = true;
    setAutoSave((current) => ({ ...current, status: "saving", error: null }));
    const photo = photoFile;
    const addOnImages = setupWizardEnabled ? pendingAddOnImages(schedule, tickets, uploadedAddOnImages.current) : [];
    const sponsorLogos = setupWizardEnabled ? pendingSponsorLogos(settings, uploadedSponsorLogos.current) : [];
    try {
      await patchSeries(false);
      uploadedPhoto.current = photo;
      addOnImages.forEach((addOn) => uploadedAddOnImages.current.add(addOn.imageFile));
      sponsorLogos.forEach((logo) => uploadedSponsorLogos.current.add(logo.file));
      setAutoSave({ status: "saved", savedAt: new Date(), error: null });
    } catch (e) {
      const errors = errorsFrom(e);
      setAutoSave((current) => ({ ...current, status: "error", error: errors.join(" ") || null }));
    } finally {
      autoSaveInFlight.current = false;
    }
  };

  useEffect(() => {
    if (!autoSaveEnabled) return;
    if (!autoSaveArmed.current) {
      autoSaveArmed.current = true;
      return;
    }
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => runAutoSave.current(), AUTOSAVE_DELAY_MS);
  }, [form, schedule, tickets, settings, photoFile, removePhoto]);

  useEffect(() => () => clearTimeout(autoSaveTimer.current), []);

  // The ref, not the state, guards a second click landing before React re-renders.
  const submit = async (propagate) => {
    if (savingRef.current) return;
    savingRef.current = true;
    clearTimeout(autoSaveTimer.current);
    setSaving(true);
    try {
      await patchSeries(propagate);
      if (tracksOwnValues) {
        savedInPlace();
        setModalOpen(false);
        setApplyDetailsOpen(false);
        finishSaving();
        return;
      }
      if (!wizardSteps) toastSuccess(propagate ? "Series updated and applied to every event." : "Series updated.");
      window.location = showUrl;
    } catch (e) {
      const errors = errorsFrom(e);
      toastError(errors.length > 0 ? errors.join(" ") : "Could not save the series. Please try again.");
      setModalOpen(false);
      finishSaving();
    }
  };

  const publish = async (publishAnyway = false) => {
    if (saving || !publishUrl) return;
    const anyway = publishAnyway === true;
    clearTimeout(autoSaveTimer.current);
    setSaving(true);
    setPublishErrors([]);
    const addOnImages = anyway ? [] : pendingAddOnImages(schedule, tickets, uploadedAddOnImages.current);
    const sponsorLogos = anyway ? [] : pendingSponsorLogos(settings, uploadedSponsorLogos.current);
    try {
      if (!anyway) await patchSeries(false);
      addOnImages.forEach((addOn) => uploadedAddOnImages.current.add(addOn.imageFile));
      sponsorLogos.forEach((logo) => uploadedSponsorLogos.current.add(logo.file));
      await axios.post(`${publishUrl}.json`, anyway ? { publish_anyway: true } : {}, { headers: { "X-CSRF-TOKEN": csrfToken } });
      window.location = showUrl;
    } catch (e) {
      const errors = errorsFrom(e);
      const warnings = (e && e.response && e.response.data && e.response.data.warnings) || [];
      setPublishWarnings(warnings);
      if (warnings.length === 0 && errors.length > 0) {
        setPublishErrors(errors);
        toastError("This series is not ready to publish yet.");
      } else if (warnings.length === 0) {
        toastError("Could not publish the series. Please try again.");
      }
      setSaving(false);
    }
  };

  const saveSeries = () => {
    if (!form.eventCount || !changesEventFields(form, savedSeries.current, { photoFile: newPhoto(), removePhoto })) return submit(false);
    setModalOpen(true);
  };

  const save = async () => {
    if (!canSave) return;
    if (seriesSettingsEnabled && wizardSteps && statementDescriptorProblem(settings.statementDescriptorSuffix)) {
      toastError("Fix the card statement descriptor before saving.");
      goToStep("settings");
      return;
    }
    if (!tracksOwnValues) return saveSeries();
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const clicked = { form, schedule, tickets, settings, photoFile, removePhoto };
    let preview;
    try {
      preview = await fetchDateUpdatePreview(DEFAULT_DATE_UPDATE);
    } catch (e) {
      const errors = errorsFrom(e);
      toastError(errors.length > 0 ? errors.join(" ") : "Could not check what changes. Please try again.");
      finishSaving();
      return;
    }
    // This page loaded before the series was cancelled; a cancelled series
    // saves through the plain series form, which a reload brings back.
    if (preview.canceled) {
      toastError("This series was just cancelled. Reload the page to keep editing.");
      finishSaving();
      return;
    }
    if (preview.changes.length > 0) {
      finishSaving();
      // This closure's state is what the preview was built from; the modal
      // compares it with the state it opens on.
      previewed.current = clicked;
      sectionEdits.current = new Map();
      return setUpdatePreview(preview);
    }
    // Saving now would send this click's state and drop anything edited
    // while the preview loaded, so preview the current state instead.
    if (Object.keys(clicked).some((key) => latest.current[key] !== clicked[key])) {
      finishSaving();
      return latest.current.save();
    }
    // The preview can't see a new photo, logo or add-on image, so those go
    // straight to every upcoming date, the modal's default.
    const files = pendingPlanFiles();
    if (files.photo || files.addOnImages.length + files.sponsorLogos.length > 0) {
      return runDateUpdate(DEFAULT_DATE_UPDATE);
    }
    finishSaving();
    // The date update is the only way a published wizard series reaches its
    // dates, so this save never asks to apply anything to them.
    return submit(false);
  };

  const cancelUpdate = () => {
    if (saving) return;
    // Prices typed for a new section live on the series' ticket types; going
    // back to editing undoes those, and keeps every other edit.
    const edits = [...sectionEdits.current.values()];
    sectionEdits.current = new Map();
    if (edits.length > 0) {
      setTickets((current) => {
        const byShowType = { ...current.byShowType };
        edits.forEach(({ showType, key, added, price }) => {
          const config = configFor(byShowType, showType);
          const ticketTypes = added
            ? config.ticketTypes.filter((type) => type.sectionKey !== key)
            : config.ticketTypes.map((type) => (type.sectionKey === key ? { ...type, price } : type));
          byShowType[showType] = { ...config, ticketTypes };
        });
        return { ...current, byShowType };
      });
    }
    previewed.current = null;
    setUpdatePreview(null);
  };

  latest.current = { form, schedule, tickets, settings, photoFile, removePhoto, save };

  // An edit made while the first preview loaded isn't in it, so the modal
  // previews again before it lets Update send that edit.
  const staleUpdatePreview = Boolean(updatePreview && previewed.current) && (
    previewed.current.form !== form || previewed.current.schedule !== schedule
    || previewed.current.tickets !== tickets || previewed.current.settings !== settings
  );

  const stepAfterTickets = options.canAccessMarketing ? "automations" : "display";
  const stepBeforeDisplay = options.canAccessMarketing ? "automations" : "tickets";

  const ctx = {
    csrfToken, teamId: props.teamId, stepAfterTickets, stepBeforeDisplay,
    options, form, setField, indexUrl, showUrl, save, canSubmit, canSave,
    saving: saving || autoSave.status === "saving", manualSaving: saving,
    autoSave, autoSaveEnabled,
    photoFile, setPhotoFile, removePhoto, setRemovePhoto,
    schedule, setSchedule, tickets, setTickets, settings, setSettings, selectedVenue, goToStep,
    setupWizardEnabled: wizardSteps, lockedPublished, published: setupWizardEnabled && !form.isDraft,
    ticketTemplates, setTicketTemplates, configuredTicketTypes, ticketSetProblems, venueDefaults, ticketsLanding, distributionTermsAgreed, setDistributionTermsAgreed,
    extraShowDetailNames, windowNames,
    seatingCharts: (selectedVenue && selectedVenue.seatingCharts) || [],
    addOnSeatingCharts: (selectedVenue && selectedVenue.addOnSeatingCharts) || [],
    seatingChartSectionsUrl: `${updateUrl}/seating_chart_sections`,
    openPlanningEvent: (showType, seatingChartId, context) =>
      axios
        .post(
          `${updateUrl}/seating_planning_events`,
          { show_type: showType, seating_chart_id: seatingChartId, ...(context && { context }) },
          { headers: { "X-CSRF-TOKEN": csrfToken } },
        )
        .then(({ data }) => data),
    publish, publishErrors, canPublish: !!publishUrl,
    publishWarnings, dismissPublishWarnings: () => setPublishWarnings([]),
    canApplyDetailsToEvents: !tracksOwnValues && !!form.eventCount && canSave,
    tracksOwnValues, seriesSettingsEnabled: seriesSettingsEnabled && wizardSteps,
    isUploaded: (file) => uploadedSponsorLogos.current.has(file),
    openApplyDetails: () => setApplyDetailsOpen(true),
  };

  const sections = {
    details: <SeriesDetailsSection />,
    schedule: <ScheduleSection />,
    tickets: <TicketConfigSection />,
    automations: <AutomationsSection />,
    display: <DisplaySection />,
    review: <ReviewSection />,
    ...(seriesSettingsEnabled && { settings: <SeriesSettingsSection /> }),
  };
  const legacySections = { details: <SeriesDetailsSection />, display: <DisplaySection /> };

  return (
    <SeriesEditContext.Provider value={ctx}>
      <div className="series-edit">
        <SeriesEditHeader />
        <div className="series-edit-body">
          <SeriesSetupNav
            active={step}
            completion={completion}
            enabled={wizardSteps}
            published={lockedPublished}
            legacyOnly={setupWizardAvailable && !setupWizardEnabled}
            settingsEnabled={seriesSettingsEnabled && wizardSteps}
            canAccessMarketing={!!options.canAccessMarketing}
            onSelect={goToStep}
          />
          <div className="series-edit-content">
            {(wizardSteps ? sections : legacySections)[step] || <SeriesDetailsSection />}
          </div>
        </div>
      </div>
      <ToastContainer />
      <ApplySeriesDetailsModal
        open={applyDetailsOpen}
        eventCount={form.eventCount}
        saving={saving}
        onConfirm={() => submit(true)}
        onCancel={() => !saving && setApplyDetailsOpen(false)}
      />
      {updatePreview && (
        <UpdateDatesModal
          seriesName={savedSeries.current.name || UNTITLED_SERIES}
          initialPreview={updatePreview}
          timeZone={form.venueTimeZone}
          currency={options.baseCurrency}
          saving={saving}
          fetchPreview={fetchDateUpdatePreview}
          onConfirm={applyDateUpdate}
          onCancel={cancelUpdate}
          sectionPrice={sectionPrice}
          onSectionPrice={setSectionPrice}
          sectionFree={sectionFree}
          matchingSectionPrice={matchingSectionPrice}
          eventTypes={options.eventTypes}
          eventTemplates={options.eventTemplates}
          pendingFiles={pendingPlanFiles()}
          stale={staleUpdatePreview}
        />
      )}
      <PropagateChangesModal
        open={modalOpen}
        eventCount={form.eventCount}
        saving={saving}
        onChoose={submit}
        onCancel={() => !saving && setModalOpen(false)}
      />
    </SeriesEditContext.Provider>
  );
};

SeriesEditApp.propTypes = {
  teamId: PropTypes.number.isRequired,
  csrfToken: PropTypes.string.isRequired,
  series: PropTypes.object.isRequired,
  options: PropTypes.object.isRequired,
  indexUrl: PropTypes.string,
  showUrl: PropTypes.string.isRequired,
  updateUrl: PropTypes.string.isRequired,
  publishUrl: PropTypes.string,
  setupConfig: PropTypes.object,
  setupWizardEnabled: PropTypes.bool,
  setupWizardAvailable: PropTypes.bool,
  seriesSettingsEnabled: PropTypes.bool,
};

export default SeriesEditApp;
